/**
 * Employee Table Component
 *
 * Displays wizard employees in an editable table with status indicators.
 *
 * Session 4.2: Step 3 - Map & Validate
 */

import {
  PencilIcon,
  TrashIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { useWizard } from '../context/WizardContext';
import type { WizardEmployee } from '../context/types';
import type { EmployeeValidationResult } from '@/types/validation';

// =============================================================================
// TYPES
// =============================================================================

interface EmployeeTableProps {
  /** Validation results keyed by employee NI */
  validationByNi?: Map<string, EmployeeValidationResult>;
  /** Callback when edit button is clicked */
  onEditEmployee: (employee: WizardEmployee) => void;
}

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Format currency for display.
 */
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

/**
 * Get status for an employee based on validation results.
 */
function getEmployeeStatus(
  employee: WizardEmployee,
  validationResult?: EmployeeValidationResult
): 'valid' | 'error' | 'warning' | 'unknown' {
  if (!validationResult) {
    // No validation yet - check basic requirements
    if (!employee.employeeNi || employee.employeeNi.length !== 8) {
      return 'warning';
    }
    return 'unknown';
  }

  if (!validationResult.is_valid) {
    return 'error';
  }

  if (validationResult.has_warnings) {
    return 'warning';
  }

  return 'valid';
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Editable employee table for wizard step 3.
 */
export function EmployeeTable({
  validationByNi,
  onEditEmployee,
}: EmployeeTableProps) {
  const { state, removeEmployee } = useWizard();
  const isHospitality = state.formatType === 'HOSPITALITY';

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12"
            >
              Status
            </th>
            <th
              scope="col"
              className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              NI Number
            </th>
            <th
              scope="col"
              className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Name
            </th>
            <th
              scope="col"
              className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              {isHospitality ? 'Basic Wages' : 'Wages'}
            </th>
            {isHospitality && (
              <th
                scope="col"
                className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Gratuity
              </th>
            )}
            <th
              scope="col"
              className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16"
            >
              Cat
            </th>
            <th
              scope="col"
              className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {state.employees.map(employee => {
            const validationResult = validationByNi?.get(employee.employeeNi);
            const status = getEmployeeStatus(employee, validationResult);

            return (
              <tr
                key={employee.id}
                className={
                  status === 'error'
                    ? 'bg-red-50'
                    : status === 'warning'
                      ? 'bg-amber-50'
                      : undefined
                }
              >
                {/* Status indicator */}
                <td className="px-4 py-3 whitespace-nowrap">
                  {status === 'error' ? (
                    <ExclamationCircleIcon
                      className="h-5 w-5 text-red-500"
                      title="Has errors"
                    />
                  ) : status === 'warning' ? (
                    <ExclamationTriangleIcon
                      className="h-5 w-5 text-amber-500"
                      title="Has warnings"
                    />
                  ) : status === 'valid' ? (
                    <CheckCircleIcon
                      className="h-5 w-5 text-green-500"
                      title="Valid"
                    />
                  ) : (
                    <span
                      className="h-5 w-5 inline-block rounded-full bg-gray-200"
                      title="Not validated"
                    />
                  )}
                </td>

                {/* NI Number */}
                <td className="px-4 py-3 whitespace-nowrap">
                  <span
                    className={`font-mono text-sm ${
                      !employee.employeeNi ? 'text-gray-400 italic' : ''
                    }`}
                  >
                    {employee.employeeNi || '(missing)'}
                  </span>
                </td>

                {/* Name */}
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {employee.lastName}
                    {employee.firstName && `, ${employee.firstName}`}
                    {employee.middleInitial && ` ${employee.middleInitial}.`}
                  </div>
                </td>

                {/* Basic Wages */}
                <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900">
                  {formatCurrency(employee.basicWages)}
                </td>

                {/* Gratuity (hospitality only) */}
                {isHospitality && (
                  <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900">
                    {employee.gratuityWages > 0
                      ? formatCurrency(employee.gratuityWages)
                      : '—'}
                  </td>
                )}

                {/* Category */}
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                    {employee.category}
                  </span>
                </td>

                {/* Actions */}
                <td className="px-4 py-3 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onEditEmployee(employee)}
                      type="button"
                      className="p-1 text-gray-400 hover:text-blue-600"
                      title="Edit"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => removeEmployee(employee.id)}
                      type="button"
                      className="p-1 text-gray-400 hover:text-red-600"
                      title="Remove"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}

          {state.employees.length === 0 && (
            <tr>
              <td
                colSpan={isHospitality ? 7 : 6}
                className="px-4 py-8 text-center text-gray-500"
              >
                No employees yet. Add employees to continue.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
